import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface ValidationReport {
  valid: boolean;
  template_filename: string;
  expected_count: number;
  found_count: number;
  coverage_percent: number;
  matched: string[];
  missing_in_file: string[];
  extra_in_file: string[];
  critical_missing: string[];
  warnings: string[];
}

interface DiscoverResult {
  mode: 'discover';
  filename: string;
  found_placeholders: string[];
  count: number;
  grouped: Record<string, string[]>;
}

interface ValidationResult {
  data: ValidationReport | DiscoverResult | null;
  error: string | null;
}

export function useTemplateValidation() {
  const [isValidating, setIsValidating] = useState(false);
  const [isDiscovering, setIsDiscovering] = useState(false);

  /**
   * Validate a template by its ID
   * Compares placeholders in the file against the database schema
   */
  const validateTemplate = async (templateId: string): Promise<ValidationResult> => {
    setIsValidating(true);
    try {
      const { data, error } = await supabase.functions.invoke('validate-template', {
        body: { templateId, mode: 'validate' }
      });

      if (error) {
        console.error('[useTemplateValidation] Validation error:', error);
        return { data: null, error: error.message };
      }

      return { data: data as ValidationReport, error: null };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Validation failed';
      console.error('[useTemplateValidation] Exception:', err);
      return { data: null, error: errorMessage };
    } finally {
      setIsValidating(false);
    }
  };

  /**
   * Validate a template by generator slug and file extension
   */
  const validateByGenerator = async (
    generatorSlug: string, 
    extension: 'docx' | 'xlsx'
  ): Promise<ValidationResult> => {
    setIsValidating(true);
    try {
      const { data, error } = await supabase.functions.invoke('validate-template', {
        body: { generatorSlug, extension, mode: 'validate' }
      });

      if (error) {
        console.error('[useTemplateValidation] Validation error:', error);
        return { data: null, error: error.message };
      }

      return { data: data as ValidationReport, error: null };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Validation failed';
      console.error('[useTemplateValidation] Exception:', err);
      return { data: null, error: errorMessage };
    } finally {
      setIsValidating(false);
    }
  };

  /**
   * Discover all placeholders in a template file without validating against schema
   * Useful for debugging and understanding template structure
   */
  const discoverPlaceholders = async (
    generatorSlug: string, 
    extension: 'docx' | 'xlsx'
  ): Promise<ValidationResult> => {
    setIsDiscovering(true);
    try {
      const { data, error } = await supabase.functions.invoke('validate-template', {
        body: { generatorSlug, extension, mode: 'discover' }
      });

      if (error) {
        console.error('[useTemplateValidation] Discover error:', error);
        return { data: null, error: error.message };
      }

      return { data: data as DiscoverResult, error: null };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Discovery failed';
      console.error('[useTemplateValidation] Exception:', err);
      return { data: null, error: errorMessage };
    } finally {
      setIsDiscovering(false);
    }
  };

  /**
   * Validate all active templates for a generator
   * Returns an array of validation results
   */
  const validateAllTemplates = async (generatorId: string): Promise<{
    results: ValidationResult[];
    allValid: boolean;
  }> => {
    setIsValidating(true);
    try {
      // First fetch all active templates for this generator
      const { data: templates, error: fetchError } = await supabase
        .from('generator_templates')
        .select('id, filename')
        .eq('generator_id', generatorId)
        .eq('is_active', true);

      if (fetchError || !templates) {
        toast.error('Failed to fetch templates');
        return { results: [], allValid: false };
      }

      if (templates.length === 0) {
        toast.warning('No active templates found');
        return { results: [], allValid: false };
      }

      // Validate each template
      const results: ValidationResult[] = [];
      let allValid = true;

      for (const template of templates) {
        const result = await validateTemplate(template.id);
        results.push(result);
        
        if (result.error || (result.data && 'valid' in result.data && !result.data.valid)) {
          allValid = false;
        }
      }

      if (allValid) {
        toast.success(`All ${templates.length} template(s) validated successfully`);
      } else {
        toast.error('Some templates failed validation');
      }

      return { results, allValid };
    } catch (err) {
      console.error('[useTemplateValidation] validateAllTemplates error:', err);
      toast.error('Validation failed');
      return { results: [], allValid: false };
    } finally {
      setIsValidating(false);
    }
  };

  return {
    validateTemplate,
    validateByGenerator,
    discoverPlaceholders,
    validateAllTemplates,
    isValidating,
    isDiscovering,
  };
}
